import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Grid,
  Paper,
  Tabs,
  Tab,
  Alert,
  IconButton,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Slider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Fab,
} from '@mui/material';
import {
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Preview as PreviewIcon,
  Settings as SettingsIcon,
  ExpandMore as ExpandMoreIcon,
  Web as WebIcon,
  Brush as BrushIcon,
  FormatSize as FormatSizeIcon,
  ViewModule as ViewModuleIcon,
  ColorLens as ColorLensIcon,
  Brightness4 as Brightness4Icon,
  Brightness7 as Brightness7Icon,
  Palette as PaletteIcon,
} from '@mui/icons-material';
import { useRbac } from '../../context/RbacContext';

interface ThemeConfig {
  id: string;
  name: string;
  isActive: boolean;
  colors: {
    primary: string;
    secondary: string;
    accent: string;
    background: string;
    surface: string;
    text: string;
    textSecondary: string;
    error: string;
    warning: string;
    success: string;
    info: string;
  };
  typography: {
    fontFamily: string;
    fontSize: {
      h1: number;
      h2: number;
      h3: number;
      h4: number;
      h5: number;
      h6: number;
      body1: number;
      body2: number;
    };
    fontWeight: {
      light: number;
      regular: number;
      medium: number;
      bold: number;
    };
  };
  layout: {
    maxWidth: number;
    borderRadius: number;
    spacing: number;
    shadows: {
      small: string;
      medium: string;
      large: string;
    };
  };
  components: {
    button: {
      borderRadius: number;
      padding: number;
    };
    card: {
      borderRadius: number;
      elevation: number;
    };
    input: {
      borderRadius: number;
      borderWidth: number;
    };
  };
}

const ThemeManagement: React.FC = () => {
  const { hasPermission } = useRbac();
  const [activeTab, setActiveTab] = useState(0);
  const [themes, setThemes] = useState<ThemeConfig[]>([]);
  const [currentTheme, setCurrentTheme] = useState<ThemeConfig | null>(null);
  const [previewMode, setPreviewMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Form states
  const [themeForm, setThemeForm] = useState<Partial<ThemeConfig>>({
    name: '',
    colors: {
      primary: '#6366f1',
      secondary: '#06b6d4',
      accent: '#f59e0b',
      background: '#ffffff',
      surface: '#f8fafc',
      text: '#1f2937',
      textSecondary: '#6b7280',
      error: '#ef4444',
      warning: '#f59e0b',
      success: '#10b981',
      info: '#3b82f6',
    },
    typography: {
      fontFamily: 'Inter, sans-serif',
      fontSize: {
        h1: 48,
        h2: 36,
        h3: 30,
        h4: 24,
        h5: 20,
        h6: 18,
        body1: 16,
        body2: 14,
      },
      fontWeight: {
        light: 300,
        regular: 400,
        medium: 500,
        bold: 700,
      },
    },
    layout: {
      maxWidth: 1200,
      borderRadius: 8,
      spacing: 16,
      shadows: {
        small: '0 1px 3px rgba(0,0,0,0.12)',
        medium: '0 4px 6px rgba(0,0,0,0.1)',
        large: '0 10px 15px rgba(0,0,0,0.1)',
      },
    },
    components: {
      button: {
        borderRadius: 8,
        padding: 12,
      },
      card: {
        borderRadius: 12,
        elevation: 2,
      },
      input: {
        borderRadius: 6,
        borderWidth: 1,
      },
    },
  });

  // Mock themes
  const mockThemes: ThemeConfig[] = [
    {
      id: '1',
      name: 'Monio Default',
      isActive: true,
      colors: {
        primary: '#6366f1',
        secondary: '#06b6d4',
        accent: '#f59e0b',
        background: '#ffffff',
        surface: '#f8fafc',
        text: '#1f2937',
        textSecondary: '#6b7280',
        error: '#ef4444',
        warning: '#f59e0b',
        success: '#10b981',
        info: '#3b82f6',
      },
      typography: {
        fontFamily: 'Inter, sans-serif',
        fontSize: {
          h1: 48,
          h2: 36,
          h3: 30,
          h4: 24,
          h5: 20,
          h6: 18,
          body1: 16,
          body2: 14,
        },
        fontWeight: {
          light: 300,
          regular: 400,
          medium: 500,
          bold: 700,
        },
      },
      layout: {
        maxWidth: 1200,
        borderRadius: 8,
        spacing: 16,
        shadows: {
          small: '0 1px 3px rgba(0,0,0,0.12)',
          medium: '0 4px 6px rgba(0,0,0,0.1)',
          large: '0 10px 15px rgba(0,0,0,0.1)',
        },
      },
      components: {
        button: {
          borderRadius: 8,
          padding: 12,
        },
        card: {
          borderRadius: 12,
          elevation: 2,
        },
        input: {
          borderRadius: 6,
          borderWidth: 1,
        },
      },
    },
    {
      id: '2',
      name: 'Dark Theme',
      isActive: false,
      colors: {
        primary: '#8b5cf6',
        secondary: '#06b6d4',
        accent: '#f59e0b',
        background: '#0f172a',
        surface: '#1e293b',
        text: '#f1f5f9',
        textSecondary: '#94a3b8',
        error: '#ef4444',
        warning: '#f59e0b',
        success: '#10b981',
        info: '#3b82f6',
      },
      typography: {
        fontFamily: 'Inter, sans-serif',
        fontSize: {
          h1: 48,
          h2: 36,
          h3: 30,
          h4: 24,
          h5: 20,
          h6: 18,
          body1: 16,
          body2: 14,
        },
        fontWeight: {
          light: 300,
          regular: 400,
          medium: 500,
          bold: 700,
        },
      },
      layout: {
        maxWidth: 1200,
        borderRadius: 12,
        spacing: 20,
        shadows: {
          small: '0 1px 3px rgba(0,0,0,0.3)',
          medium: '0 4px 6px rgba(0,0,0,0.3)',
          large: '0 10px 15px rgba(0,0,0,0.3)',
        },
      },
      components: {
        button: {
          borderRadius: 12,
          padding: 16,
        },
        card: {
          borderRadius: 16,
          elevation: 4,
        },
        input: {
          borderRadius: 8,
          borderWidth: 2,
        },
      },
    },
  ];

  useEffect(() => {
    setThemes(mockThemes);
    setCurrentTheme(mockThemes.find(theme => theme.isActive) || null);
  }, []);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleThemeChange = (theme: ThemeConfig) => {
    setCurrentTheme(theme);
    setThemeForm(theme);
  };

  const handleColorChange = (colorKey: string, value: string) => {
    setThemeForm({
      ...themeForm,
      colors: {
        ...themeForm.colors!,
        [colorKey]: value,
      },
    });
  };

  const handleTypographyChange = (key: string, value: any) => {
    setThemeForm({
      ...themeForm,
      typography: {
        ...themeForm.typography!,
        [key]: value,
      },
    });
  };

  const handleLayoutChange = (key: string, value: any) => {
    setThemeForm({
      ...themeForm,
      layout: {
        ...themeForm.layout!,
        [key]: value,
      },
    });
  };

  const handleComponentChange = (component: string, key: string, value: any) => {
    const currentComponents = themeForm.components || {
      button: { borderRadius: 8, padding: 12 },
      card: { borderRadius: 12, elevation: 2 },
      input: { borderRadius: 6, borderWidth: 1 },
    };
    const currentComponent = currentComponents[component as keyof typeof currentComponents] || {};
    
    setThemeForm({
      ...themeForm,
      components: {
        ...currentComponents,
        [component]: {
          ...currentComponent,
          [key]: value,
        },
      },
    });
  };

  const handleSaveTheme = () => {
    setLoading(true);
    // Save theme logic here
    setTimeout(() => {
      setLoading(false);
      setSuccess('Theme saved successfully!');
      setTimeout(() => setSuccess(null), 3000);
    }, 1000);
  };

  const handlePreviewTheme = () => {
    setPreviewMode(!previewMode);
  };

  const handleResetTheme = () => {
    if (currentTheme) {
      setThemeForm(currentTheme);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Theme Management
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}

      {/* Theme Selection */}
      <Card sx={{ mb: 3 }}>
        <CardHeader title="Active Theme" />
        <CardContent>
          <Grid container spacing={2}>
            {themes.map((theme) => (
              <Grid item xs={12} sm={6} md={4} key={theme.id}>
                <Card
                  sx={{
                    cursor: 'pointer',
                    border: theme.isActive ? '2px solid #6366f1' : '1px solid #e5e7eb',
                    '&:hover': {
                      borderColor: '#6366f1',
                    },
                  }}
                  onClick={() => handleThemeChange(theme)}
                >
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {theme.name}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                      <Box
                        sx={{
                          width: 20,
                          height: 20,
                          borderRadius: '50%',
                          backgroundColor: theme.colors.primary,
                        }}
                      />
                      <Box
                        sx={{
                          width: 20,
                          height: 20,
                          borderRadius: '50%',
                          backgroundColor: theme.colors.secondary,
                        }}
                      />
                      <Box
                        sx={{
                          width: 20,
                          height: 20,
                          borderRadius: '50%',
                          backgroundColor: theme.colors.accent,
                        }}
                      />
                    </Box>
                    <Chip
                      label={theme.isActive ? 'Active' : 'Inactive'}
                      color={theme.isActive ? 'success' : 'default'}
                      size="small"
                    />
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      {/* Theme Editor */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab label="Colors" icon={<PaletteIcon />} />
          <Tab label="Typography" icon={<FormatSizeIcon />} />
          <Tab label="Layout" icon={<ViewModuleIcon />} />
          <Tab label="Components" icon={<SettingsIcon />} />
        </Tabs>
      </Paper>

      {/* Colors Tab */}
      {activeTab === 0 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardHeader title="Primary Colors" />
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Primary"
                      value={themeForm.colors?.primary}
                      onChange={(e) => handleColorChange('primary', e.target.value)}
                      type="color"
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Secondary"
                      value={themeForm.colors?.secondary}
                      onChange={(e) => handleColorChange('secondary', e.target.value)}
                      type="color"
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Accent"
                      value={themeForm.colors?.accent}
                      onChange={(e) => handleColorChange('accent', e.target.value)}
                      type="color"
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Background"
                      value={themeForm.colors?.background}
                      onChange={(e) => handleColorChange('background', e.target.value)}
                      type="color"
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardHeader title="Semantic Colors" />
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Success"
                      value={themeForm.colors?.success}
                      onChange={(e) => handleColorChange('success', e.target.value)}
                      type="color"
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Error"
                      value={themeForm.colors?.error}
                      onChange={(e) => handleColorChange('error', e.target.value)}
                      type="color"
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Warning"
                      value={themeForm.colors?.warning}
                      onChange={(e) => handleColorChange('warning', e.target.value)}
                      type="color"
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Info"
                      value={themeForm.colors?.info}
                      onChange={(e) => handleColorChange('info', e.target.value)}
                      type="color"
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Typography Tab */}
      {activeTab === 1 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardHeader title="Font Settings" />
              <CardContent>
                <TextField
                  fullWidth
                  label="Font Family"
                  value={themeForm.typography?.fontFamily}
                  onChange={(e) => handleTypographyChange('fontFamily', e.target.value)}
                  margin="normal"
                />
                
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  Font Sizes
                </Typography>
                
                {Object.entries(themeForm.typography?.fontSize || {}).map(([key, value]) => (
                  <Box key={key} sx={{ mb: 2 }}>
                    <Typography variant="body2" gutterBottom>
                      {key.toUpperCase()}
                    </Typography>
                    <Slider
                      value={value}
                      onChange={(e, newValue) => handleTypographyChange('fontSize', {
                        ...themeForm.typography?.fontSize,
                        [key]: newValue,
                      })}
                      min={12}
                      max={72}
                      step={1}
                      valueLabelDisplay="auto"
                    />
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardHeader title="Font Weights" />
              <CardContent>
                {Object.entries(themeForm.typography?.fontWeight || {}).map(([key, value]) => (
                  <Box key={key} sx={{ mb: 2 }}>
                    <Typography variant="body2" gutterBottom>
                      {key.charAt(0).toUpperCase() + key.slice(1)}
                    </Typography>
                    <Slider
                      value={value}
                      onChange={(e, newValue) => handleTypographyChange('fontWeight', {
                        ...themeForm.typography?.fontWeight,
                        [key]: newValue,
                      })}
                      min={100}
                      max={900}
                      step={100}
                      valueLabelDisplay="auto"
                    />
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Layout Tab */}
      {activeTab === 2 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardHeader title="Layout Settings" />
              <CardContent>
                <TextField
                  fullWidth
                  label="Max Width (px)"
                  type="number"
                  value={themeForm.layout?.maxWidth}
                  onChange={(e) => handleLayoutChange('maxWidth', parseInt(e.target.value))}
                  margin="normal"
                />
                
                <Typography variant="body2" gutterBottom sx={{ mt: 2 }}>
                  Border Radius: {themeForm.layout?.borderRadius}px
                </Typography>
                <Slider
                  value={themeForm.layout?.borderRadius || 8}
                  onChange={(e, newValue) => handleLayoutChange('borderRadius', newValue)}
                  min={0}
                  max={24}
                  step={1}
                  valueLabelDisplay="auto"
                />
                
                <Typography variant="body2" gutterBottom sx={{ mt: 2 }}>
                  Spacing: {themeForm.layout?.spacing}px
                </Typography>
                <Slider
                  value={themeForm.layout?.spacing || 16}
                  onChange={(e, newValue) => handleLayoutChange('spacing', newValue)}
                  min={4}
                  max={32}
                  step={2}
                  valueLabelDisplay="auto"
                />
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardHeader title="Shadows" />
              <CardContent>
                <TextField
                  fullWidth
                  label="Small Shadow"
                  value={themeForm.layout?.shadows.small}
                  onChange={(e) => handleLayoutChange('shadows', {
                    ...themeForm.layout?.shadows,
                    small: e.target.value,
                  })}
                  margin="normal"
                />
                <TextField
                  fullWidth
                  label="Medium Shadow"
                  value={themeForm.layout?.shadows.medium}
                  onChange={(e) => handleLayoutChange('shadows', {
                    ...themeForm.layout?.shadows,
                    medium: e.target.value,
                  })}
                  margin="normal"
                />
                <TextField
                  fullWidth
                  label="Large Shadow"
                  value={themeForm.layout?.shadows.large}
                  onChange={(e) => handleLayoutChange('shadows', {
                    ...themeForm.layout?.shadows,
                    large: e.target.value,
                  })}
                  margin="normal"
                />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Components Tab */}
      {activeTab === 3 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Card>
              <CardHeader title="Button Settings" />
              <CardContent>
                <Typography variant="body2" gutterBottom>
                  Border Radius: {themeForm.components?.button.borderRadius}px
                </Typography>
                <Slider
                  value={themeForm.components?.button.borderRadius || 8}
                  onChange={(e, newValue) => handleComponentChange('button', 'borderRadius', newValue)}
                  min={0}
                  max={24}
                  step={1}
                  valueLabelDisplay="auto"
                />
                
                <Typography variant="body2" gutterBottom sx={{ mt: 2 }}>
                  Padding: {themeForm.components?.button.padding}px
                </Typography>
                <Slider
                  value={themeForm.components?.button.padding || 12}
                  onChange={(e, newValue) => handleComponentChange('button', 'padding', newValue)}
                  min={8}
                  max={24}
                  step={2}
                  valueLabelDisplay="auto"
                />
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card>
              <CardHeader title="Card Settings" />
              <CardContent>
                <Typography variant="body2" gutterBottom>
                  Border Radius: {themeForm.components?.card.borderRadius}px
                </Typography>
                <Slider
                  value={themeForm.components?.card.borderRadius || 12}
                  onChange={(e, newValue) => handleComponentChange('card', 'borderRadius', newValue)}
                  min={0}
                  max={24}
                  step={1}
                  valueLabelDisplay="auto"
                />
                
                <Typography variant="body2" gutterBottom sx={{ mt: 2 }}>
                  Elevation: {themeForm.components?.card.elevation}
                </Typography>
                <Slider
                  value={themeForm.components?.card.elevation || 2}
                  onChange={(e, newValue) => handleComponentChange('card', 'elevation', newValue)}
                  min={0}
                  max={8}
                  step={1}
                  valueLabelDisplay="auto"
                />
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card>
              <CardHeader title="Input Settings" />
              <CardContent>
                <Typography variant="body2" gutterBottom>
                  Border Radius: {themeForm.components?.input.borderRadius}px
                </Typography>
                <Slider
                  value={themeForm.components?.input.borderRadius || 6}
                  onChange={(e, newValue) => handleComponentChange('input', 'borderRadius', newValue)}
                  min={0}
                  max={16}
                  step={1}
                  valueLabelDisplay="auto"
                />
                
                <Typography variant="body2" gutterBottom sx={{ mt: 2 }}>
                  Border Width: {themeForm.components?.input.borderWidth}px
                </Typography>
                <Slider
                  value={themeForm.components?.input.borderWidth || 1}
                  onChange={(e, newValue) => handleComponentChange('input', 'borderWidth', newValue)}
                  min={0}
                  max={4}
                  step={1}
                  valueLabelDisplay="auto"
                />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Preview Section */}
      <Card sx={{ mt: 3 }}>
        <CardHeader 
          title="Theme Preview" 
          action={
            <Button
              variant="outlined"
              startIcon={previewMode ? <Brightness7Icon /> : <Brightness4Icon />}
              onClick={handlePreviewTheme}
            >
              {previewMode ? 'Light Preview' : 'Dark Preview'}
            </Button>
          }
        />
        <CardContent>
          <Box
            sx={{
              p: 3,
              borderRadius: themeForm.layout?.borderRadius,
              backgroundColor: themeForm.colors?.background,
              color: themeForm.colors?.text,
              fontFamily: themeForm.typography?.fontFamily,
              maxWidth: themeForm.layout?.maxWidth,
              mx: 'auto',
            }}
          >
            <Typography 
              variant="h1" 
              sx={{ 
                fontSize: themeForm.typography?.fontSize.h1,
                fontWeight: themeForm.typography?.fontWeight.bold,
                color: themeForm.colors?.primary,
                mb: 2,
              }}
            >
              Sample Heading
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                fontSize: themeForm.typography?.fontSize.body1,
                fontWeight: themeForm.typography?.fontWeight.regular,
                color: themeForm.colors?.textSecondary,
                mb: 3,
              }}
            >
              This is a sample text to preview how your typography settings will look.
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                sx={{
                  backgroundColor: themeForm.colors?.primary,
                  borderRadius: themeForm.components?.button.borderRadius,
                  px: themeForm.components?.button.padding,
                }}
              >
                Primary Button
              </Button>
              <Button
                variant="outlined"
                sx={{
                  borderColor: themeForm.colors?.secondary,
                  color: themeForm.colors?.secondary,
                  borderRadius: themeForm.components?.button.borderRadius,
                  px: themeForm.components?.button.padding,
                }}
              >
                Secondary Button
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={handleResetTheme}
        >
          Reset
        </Button>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSaveTheme}
          disabled={loading}
        >
          {loading ? 'Saving...' : 'Save Theme'}
        </Button>
      </Box>

      {/* Floating Action Button */}
      <Fab
        color="primary"
        aria-label="preview"
        sx={{ position: 'fixed', bottom: 16, right: 16 }}
        onClick={handlePreviewTheme}
      >
        <PreviewIcon />
      </Fab>
    </Box>
  );
};

export default ThemeManagement; 